import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    Typography,
} from '@mui/material';
import { useSetPinMutation } from '../api/JsdhNotifikaceService/service/AuthMutation';

interface SetPinDialogProps {
    open: boolean;
    onClose: () => void;
}

const SetPinDialog: React.FC<SetPinDialogProps> = ({ open, onClose }) => {
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const setPinMutation = useSetPinMutation();

    const resetForm = () => {
        setPin('');
        setConfirmPin('');
        setValidationError(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setPin(value);
    };

    const handleConfirmPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setConfirmPin(value);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        if (!pin) {
            setValidationError('PIN je povinný.');
            return;
        }

        if (pin.length < 4 || pin.length > 6) {
            setValidationError('PIN musí mít 4-6 číslic.');
            return;
        }

        if (pin !== confirmPin) {
            setValidationError('PINy se neshodují.');
            return;
        }

        setPinMutation.mutate(pin, {
            onSuccess: () => {
                handleClose();
            },
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Nastavit PIN</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Typography variant="body2" color="text.secondary">
                            PIN slouží k rychlému přihlášení v mobilní aplikaci.
                        </Typography>
                        {validationError && (
                            <Alert severity="error">{validationError}</Alert>
                        )}
                        <TextField
                            label="Nový PIN"
                            value={pin}
                            onChange={handlePinChange}
                            fullWidth
                            required
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 6,
                            }}
                            helperText="4-6 číslic"
                            error={pin.length > 0 && (pin.length < 4 || pin.length > 6)}
                        />
                        <TextField
                            label="Potvrzení PINu"
                            value={confirmPin}
                            onChange={handleConfirmPinChange}
                            fullWidth
                            required
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 6,
                            }}
                            error={confirmPin.length > 0 && pin !== confirmPin}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={setPinMutation.isPending}>
                        Zrušit
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={setPinMutation.isPending || pin.length < 4 || pin !== confirmPin}
                    >
                        {setPinMutation.isPending ? 'Ukládám...' : 'Nastavit PIN'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SetPinDialog;
